import { Css, ICustomTheme } from '@/ui';
import { Color } from './Color';
import {
	FONT_FAMILY_AVENIR_NEXT,
	FONT_FAMILY_BEBAS_NEUE,
	FONT_FAMILY_INTER,
} from './Font';
import { Z_INDEX_BASE, Z_INDEX_DROPDOWN } from './zIndex';
import { Shadow } from './Shadow';

export const theme: ICustomTheme = {
	layout: {
		page: {
			padding: '0',
			background: Color.OnPrimarySurface,
		},
		image: {
			margin: `${Css.pxToRem(24)} 0 ${Css.pxToRem(16)}`,
			maxWidth: '65%',
			mobile: {
				margin: `${Css.pxToRem(16)} 0 ${Css.pxToRem(32)}`,
				maxWidth: '100%',
			},
			tablet: {
				maxWidth: '100%',
			},
		},
	},
	background: {
		default: Color.OnPrimarySurface,
	},
	callToAction: {
		padding: `${Css.pxToRem(32)}`,
		borderRadius: '16px',
		background: {
			default: Color.Neutral94,
		},
		mobile: {
			padding: `${Css.pxToRem(32)}`,
		},
	},
	color: {
		highlight: Color.PrimaryBlue,
		brand: Color.PrimaryBlue,
		background: {
			default: Color.OnPrimarySurface,
			light: Color.PrimaryBlue87,
		},
		text: {
			default: Color.OnBackgroundText,
			inverted: Color.OnPrimarySurface,
			light: Color.Neutral40,
		},
		border: {
			default: `1px solid ${Color.Neutral80}`,
		},
	},
	grid: {
		gutter: {
			horizontal: {
				default: Css.pxToRem(24),
				mobile: Css.pxToRem(16),
			},
			vertical: {
				default: Css.pxToRem(24),
			},
		},
	},
	accordion: {
		title: {
			padding: `${Css.pxToRem(22)} 0 ${Css.pxToRem(16)}`,
			fontFamily: `${FONT_FAMILY_AVENIR_NEXT}, Arial, Roboto, sans-serif`,
		},
		content: {
			padding: `${Css.pxToRem(16)} 0 ${Css.pxToRem(8)}`,
		},
	},
	typography: {
		body: {
			fontFamily: `${FONT_FAMILY_INTER}, Arial, Roboto, sans-serif`,
			size: '16px',
			lineHeight: '1.375', // Unitless line height (1.375 * 16px = 22px)
			color: Color.OnBackgroundText,
			fontWeight: '300',
		},

		paragraph: {
			margin: '0 0 1.5em 0',
		},

		header: {
			margin: '0 0 0.4em 0',
			h1: {
				size: Css.pxToRem(70),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_AVENIR_NEXT}, Arial, Roboto, sans-serif`,
				fontWeight: '700',
			},
			h2: {
				size: Css.pxToRem(54),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_AVENIR_NEXT}, Arial, Roboto, sans-serif`,
				fontWeight: '700',
			},
			h3: {
				size: Css.pxToRem(52),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_BEBAS_NEUE}, Arial, Roboto, sans-serif`,
				fontWeight: '400',
			},
			h4: {
				size: Css.pxToRem(40),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_BEBAS_NEUE}, Arial, Roboto, sans-serif`,
				fontWeight: '400',
			},
			h5: {
				size: Css.pxToRem(29),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_BEBAS_NEUE}, Arial, Roboto, sans-serif`,
				fontWeight: '400',
			},
			h6: {
				size: Css.pxToRem(22),
				color: Color.OnBackgroundText,
				fontFamily: `${FONT_FAMILY_AVENIR_NEXT}, Arial, Roboto, sans-serif`,
				fontWeight: 700,
			},
		},
		mobile: {
			header: {
				h1: {
					size: Css.pxToRem(70),
				},
				h2: {
					size: Css.pxToRem(54),
				},
				h3: {
					size: Css.pxToRem(52),
				},
				h4: {
					size: Css.pxToRem(40),
				},
				h5: {
					size: Css.pxToRem(29),
				},
				h6: {
					size: Css.pxToRem(22),
				},
			},
		},
		link: {
			hover: {
				color: Color.PrimaryBlue,
			},
			color: Color.PrimaryBlue,
		},
		small: {
			size: Css.pxToRem(14),
			weight: '400',
		},
	},
	card: {
		boxShadow: Shadow.Medium,
		contentMargin: `${Css.pxToRem(24)} ${Css.pxToRem(16)}`,
		borderRadius: '16px',
		fullyClickable: {
			boxShadow: Shadow.Large,
			hover: {
				boxShadow: Shadow.Large,
			},
		},
		flowy: {
			boxShadow: Shadow.Large,
		},
	},
	blockquote: {
		boxShadow: `inset ${Css.pxToRem(4)} 0 0 0 ${Color.SecondaryGreen2}`,
		paragraph: { color: Color.OnBackgroundText, size: Css.pxToRem(16) },
		padding: `0px ${Css.pxToRem(16)}`,
		margin: '0 0 1.5em 0',
		background: 'transparent',
	},
	alertBanner: {
		background: Color.Error96,
		title: {
			fontFamily: `${FONT_FAMILY_AVENIR_NEXT}, Arial, Roboto, sans-serif`,
			fontSize: Css.pxToRem(22),
		},
	},

	button: {
		iconSeparator: '.4em',
		iconSize: Css.pxToRem(22),
		radius: '8px',
		padding: `${Css.pxToRem(12)} ${Css.pxToRem(16)}`,

		primary: {
			background: Color.PrimaryBlue,
			border: 'none',
			font: {
				color: Color.PrimaryWhite,
				weight: '500',
				fontFamily: `${FONT_FAMILY_INTER}, Arial, Roboto, sans-serif`,
			},
			hover: {
				background: Color.PrimaryBlue77,
				boxShadow: Shadow.ExtraSmall,
				font: {
					color: Color.PrimaryBlueDark,
				},
			},
			focus: {
				background: Color.PrimaryBlue87,
				boxShadow: Shadow.ExtraSmall,
				border: `1px solid ${Color.PrimaryBlue}`,
				font: {
					color: Color.PrimaryBlueDark,
				},
			},
			active: {
				background: Color.PrimaryBlue87,
				boxShadow: 'none',
			},
			disabled: {
				background: Color.Neutral94,
				opacity: '1',
				font: {
					color: Color.Neutral30,
				},
			},
			inverted: {
				background: Color.OnPrimarySurface,
				border: 'none',
				font: {
					color: Color.PrimaryBlue,
					weight: '500',
				},
				hover: {
					background: Color.PrimaryBlue77,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.PrimaryBlueDark,
					},
				},
				focus: {
					background: Color.PrimaryBlue87,
					boxShadow: Shadow.ExtraSmall,
				},
				active: {
					background: Color.PrimaryBlue87,
					boxShadow: 'none',
				},
			},
			invertedSecondary: {
				background: Color.OnPrimarySurface,
				border: `1px solid ${Color.OnPrimarySurface}`,
				font: {
					color: Color.OnSecondaryGreen2,
					weight: '500',
				},
				hover: {
					border: `1px solid ${Color.SecondaryGreen227}`,
					background: Color.SecondaryGreen227,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.OnPrimarySurface,
					},
				},
				focus: {
					border: 'none',
					background: Color.OnSecondaryGreen2,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.OnPrimarySurface,
					},
				},
				active: {
					background: Color.OnSecondaryGreen2,
					boxShadow: 'none',
					font: {
						color: Color.OnPrimarySurface,
					},
				},
			},
		},
		secondary: {
			border: `1px solid ${Color.PrimaryBlue}`,
			font: {
				color: Color.PrimaryBlue,
				weight: '500',
				fontFamily: `${FONT_FAMILY_INTER}, Arial, Roboto, sans-serif`,
			},

			hover: {
				border: `1px solid ${Color.PrimaryBlueDark}`,
				boxShadow: Shadow.ExtraSmall,
				font: {
					color: Color.PrimaryBlueDark,
				},
				background: Color.PrimaryBlue77,
			},
			focus: {
				background: Color.PrimaryBlue87,
				boxShadow: Shadow.ExtraSmall,
			},
			active: {
				border: `1px solid ${Color.PrimaryBlueDark}`,
				background: Color.PrimaryBlue87,
				boxShadow: 'none',
			},
			disabled: {
				border: `1px solid ${Color.Neutral50}`,
				opacity: '1',
				font: {
					color: Color.Neutral30,
				},
			},
			inverted: {
				border: `1px solid ${Color.OnPrimarySurface}`,
				font: {
					color: Color.OnPrimarySurface,
					weight: '500',
				},
				hover: {
					border: `1px solid ${Color.PrimaryBlueDark}`,
					background: Color.PrimaryBlue77,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.PrimaryBlueDark,
					},
				},
				focus: {
					background: Color.PrimaryBlue87,
					boxShadow: Shadow.ExtraSmall,
				},
				active: {
					background: Color.PrimaryBlue87,
					boxShadow: 'none',
				},
			},
			invertedSecondary: {
				border: `1px solid ${Color.OnSecondaryGreen2}`,
				font: {
					color: Color.OnSecondaryGreen2,
					weight: '500',
				},
				hover: {
					border: `1px solid ${Color.SecondaryGreen227}`,
					background: Color.SecondaryGreen227,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.OnPrimarySurface,
					},
				},
				focus: {
					background: Color.OnSecondaryGreen2,
					boxShadow: Shadow.ExtraSmall,
					font: {
						color: Color.OnPrimarySurface,
					},
				},
				active: {
					background: Color.OnSecondaryGreen2,
					boxShadow: 'none',
					font: {
						color: Color.OnPrimarySurface,
					},
				},
			},
		},
	},
	zIndex: {
		base: `${Z_INDEX_BASE}`,
		dropdown: `${Z_INDEX_DROPDOWN}`,
	},
	select: {
		main: {
			background: Color.Neutral94,
			color: Color.OnBackgroundText,
			borderRadius: '8px',
			boxShadow: 'none',
			borderBottom: `2px solid transparent`,
			padding: '14px',
		},

		focus: {
			boxShadow: 'none',
			borderBottom: `2px solid ${Color.PrimaryBlue}`,
			borderRadius: '8px',
		},
	},
	input: {
		placeholder: {
			color: Color.PrimaryBlue,
			focussed: {
				color: Color.PrimaryBlue,
			},
		},
	},
	dropdownList: {
		option: {
			highlighted: {
				color: Color.PrimaryBlue,
			},
		},
	},
};
